<template>
  <HrPageLayout
    title="Short Term Employment"
    subtitle="Fill, save and download PSC 38A contracts"
    icon="📝"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Public Service Board' },
      { label: 'Short Term Employment' }
    ]"
  >
    <div class="short-term-wrap">
    <div class="card mb-3">
      <div class="card-body">
        <h4 class="mb-1">Short Term Employment (PSC 38A)</h4>
        <p class="text-muted mb-0">
          Fill the framework form, save it, and download/print the signed contract.
        </p>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label class="form-label mb-1">Audit year</label>
            <input v-model.number="filters.year" type="number" min="1990" max="2100" class="form-control">
          </div>
          <div class="ms-auto d-flex gap-2">
            <button class="btn btn-outline-primary" type="button" @click="loadRows">Refresh</button>
            <button class="btn btn-primary" type="button" @click="startNew">New Contract</button>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body table-responsive">
        <table class="table table-bordered table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>ID No</th>
              <th>Job Title</th>
              <th>County / MDA</th>
              <th>Start Date</th>
              <th>Months</th>
              <th>Created</th>
              <th style="width: 210px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id">
              <td>{{ fullName(r) }}</td>
              <td>{{ r.id_no || "-" }}</td>
              <td>{{ r.job_title || "-" }}</td>
              <td>{{ r.mda_county || "-" }}</td>
              <td>{{ r.start_date || "-" }}</td>
              <td>{{ r.contract_months || "-" }}</td>
              <td>{{ r.created_at || "-" }}</td>
              <td>
                <div class="d-flex gap-1">
                  <button class="btn btn-sm btn-outline-primary" type="button" @click="editRow(r)">Edit</button>
                  <button class="btn btn-sm btn-outline-info" type="button" @click="downloadPdf(r)">PDF</button>
                  <button class="btn btn-sm btn-outline-success" type="button" @click="downloadForm(r)">Download</button>
                </div>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="8" class="text-center text-muted">No short term contracts found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <h5 class="mb-3">{{ form.id ? "Edit Contract" : "Create Contract" }}</h5>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label">Ministry/Department/Agency/County</label>
            <input v-model="form.mda_county" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Surname</label>
            <input v-model="form.surname" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">First Name</label>
            <input v-model="form.first_name" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Other Names</label>
            <input v-model="form.other_names" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Date of Birth</label>
            <input v-model="form.date_of_birth" type="date" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Gender</label>
            <select v-model="form.gender" class="form-select">
              <option value="">Select</option>
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">ID No</label>
            <input v-model="form.id_no" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">KRA PIN</label>
            <input v-model="form.kra_pin" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Nationality</label>
            <input v-model="form.nationality" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Ethnicity</label>
            <input v-model="form.ethnicity" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Home County</label>
            <input v-model="form.home_county" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Sub-County</label>
            <input v-model="form.sub_county" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Constituency</label>
            <input v-model="form.constituency" type="text" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">Postal Address</label>
            <input v-model="form.postal_address" type="text" class="form-control">
          </div>
          <div class="col-md-2">
            <label class="form-label">Code</label>
            <input v-model="form.postal_code" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Town/City</label>
            <input v-model="form.town_city" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Mobile Phone</label>
            <input v-model="form.mobile_phone" type="text" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">Email</label>
            <input v-model="form.email_address" type="email" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">Living with disability?</label>
            <select v-model="form.disability_status" class="form-select">
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Disability details</label>
            <input v-model="form.disability_details" type="text" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">NCPWD Reg No</label>
            <input v-model="form.disability_reg_no" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">NCPWD Reg Date</label>
            <input v-model="form.disability_reg_date" type="date" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">Job Title</label>
            <input v-model="form.job_title" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Start Date</label>
            <input v-model="form.start_date" type="date" class="form-control">
          </div>
          <div class="col-md-2">
            <label class="form-label">Contract Months</label>
            <input v-model.number="form.contract_months" type="number" min="1" max="6" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Salary (KES)</label>
            <input v-model="form.salary_amount" type="number" min="0" step="0.01" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">Remuneration Interval</label>
            <input v-model="form.remuneration_interval" type="text" class="form-control" placeholder="e.g. Monthly">
          </div>
          <div class="col-md-4">
            <label class="form-label">Place of Work</label>
            <input v-model="form.place_of_work" type="text" class="form-control">
          </div>
          <div class="col-md-4">
            <label class="form-label">Reporting To</label>
            <input v-model="form.reporting_to" type="text" class="form-control">
          </div>
          <div class="col-12">
            <label class="form-label">Duties (one per line)</label>
            <textarea v-model="form.duties_text" rows="3" class="form-control" placeholder="i. ...&#10;ii. ..."></textarea>
          </div>
          <div class="col-12">
            <label class="form-label">Termination Clause</label>
            <textarea v-model="form.termination_clause" rows="2" class="form-control"></textarea>
          </div>

          <div class="col-md-3">
            <label class="form-label">Employee Name</label>
            <input v-model="form.employee_sign_name" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Employee Sign Date</label>
            <input v-model="form.employee_sign_date" type="date" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Employee Witness</label>
            <input v-model="form.employee_witness_name" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Employee Witness Date</label>
            <input v-model="form.employee_witness_date" type="date" class="form-control">
          </div>

          <div class="col-md-3">
            <label class="form-label">Authorized Officer</label>
            <input v-model="form.authorized_officer_name" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Officer Designation</label>
            <input v-model="form.authorized_officer_designation" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Officer Sign Date</label>
            <input v-model="form.authorized_officer_sign_date" type="date" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Gov Witness Name</label>
            <input v-model="form.gov_witness_name" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Gov Witness Designation</label>
            <input v-model="form.gov_witness_designation" type="text" class="form-control">
          </div>
          <div class="col-md-3">
            <label class="form-label">Gov Witness Date</label>
            <input v-model="form.gov_witness_date" type="date" class="form-control">
          </div>
        </div>

        <div class="d-flex gap-2 mt-3">
          <button class="btn btn-primary" type="button" :disabled="saving" @click="saveRow">
            {{ saving ? "Saving..." : "Save Contract" }}
          </button>
          <button class="btn btn-outline-info" type="button" :disabled="!form.id" @click="downloadPdf(form)">
            Download PDF
          </button>
          <button class="btn btn-outline-success" type="button" :disabled="!form.id" @click="downloadForm(form)">
            Download Filled Form
          </button>
        </div>
        <p v-if="message" class="mt-2 mb-0" :class="ok ? 'text-success' : 'text-danger'">{{ message }}</p>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

const makeDefaultForm = () => ({
  id: 0,
  mda_county: "",
  surname: "",
  first_name: "",
  other_names: "",
  date_of_birth: "",
  gender: "",
  id_no: "",
  kra_pin: "",
  nationality: "",
  ethnicity: "",
  home_county: "",
  sub_county: "",
  constituency: "",
  postal_address: "",
  postal_code: "",
  town_city: "",
  mobile_phone: "",
  email_address: "",
  disability_status: "",
  disability_details: "",
  disability_reg_no: "",
  disability_reg_date: "",
  job_title: "",
  start_date: "",
  contract_months: 1,
  salary_amount: "",
  remuneration_interval: "Monthly",
  place_of_work: "",
  reporting_to: "",
  duties_text: "",
  termination_clause: "Either party shall give one (1) month notice in writing or one month's salary in lieu of notice.",
  employee_sign_name: "",
  employee_sign_date: "",
  employee_witness_name: "",
  employee_witness_date: "",
  authorized_officer_name: "",
  authorized_officer_designation: "",
  authorized_officer_sign_date: "",
  gov_witness_name: "",
  gov_witness_designation: "",
  gov_witness_date: ""
});

export default {
  name: "ModuleShortTermEmployment",
  components: { HrPageLayout },
  data() {
    return {
      rows: [],
      filters: { year: new Date().getFullYear() },
      form: makeDefaultForm(),
      saving: false,
      ok: false,
      message: ""
    };
  },
  mounted() {
    this.loadRows();
  },
  methods: {
    fullName(r) {
      return [r.surname, r.first_name, r.other_names].filter(Boolean).join(" ");
    },
    startNew() {
      this.form = makeDefaultForm();
      this.form.contract_months = 1;
      this.message = "";
    },
    editRow(row) {
      this.form = { ...makeDefaultForm(), ...row };
      this.message = "";
    },
    async loadRows() {
      try {
        const fd = new FormData();
        fd.append("function", "listShortTermEmploymentContracts");
        fd.append("year", String(this.filters.year || ""));
        const res = await execute(fd, authUrl);
        this.rows = res.data?.success ? (res.data.data?.rows || []) : [];
      } catch (_) {
        this.rows = [];
      }
    },
    async saveRow() {
      if (!this.form.job_title || !this.form.start_date || !this.form.mda_county) {
        this.ok = false;
        this.message = "Ministry/County, Job Title and Start Date are required.";
        return;
      }
      if (Number(this.form.contract_months || 0) < 1 || Number(this.form.contract_months || 0) > 6) {
        this.ok = false;
        this.message = "Contract period must be between 1 and 6 months.";
        return;
      }
      this.saving = true;
      this.message = "";
      try {
        const fd = new FormData();
        fd.append("function", "saveShortTermEmploymentContract");
        Object.keys(this.form).forEach((k) => fd.append(k, this.form[k] == null ? "" : String(this.form[k])));
        const res = await execute(fd, authUrl);
        this.ok = !!res.data?.success;
        this.message = res.data?.message || (this.ok ? "Saved." : "Save failed.");
        if (this.ok && res.data?.data?.id) {
          this.form.id = Number(res.data.data.id);
          await this.loadRows();
        }
      } catch (e) {
        this.ok = false;
        this.message = e?.message || "Save failed.";
      } finally {
        this.saving = false;
      }
    },
    esc(v) {
      return String(v == null ? "" : v)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    nl(v) {
      return this.esc(v).replace(/\n/g, "<br>");
    },
    async downloadPdf(row) {
      const r = { ...makeDefaultForm(), ...row };
      const jsPDF = (await import("jspdf")).default;
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const left = 42;
      const maxWidth = 510;
      let y = 42;

      const addLine = (text, size = 10, gap = 16) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(String(text || ""), maxWidth);
        lines.forEach((ln) => {
          if (y > 800) {
            doc.addPage();
            y = 42;
          }
          doc.text(ln, left, y);
          y += gap;
        });
      };

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("REPUBLIC OF KENYA", left, y);
      y += 20;
      doc.text("SHORT TERM EMPLOYMENT CONTRACT FORM (PSC 38A)", left, y);
      y += 24;

      addLine(`Ministry/Department/Agency/County: ${r.mda_county}`);
      addLine("SECTION ONE: PERSONAL DETAILS", 11);
      addLine(`Name: ${[r.surname, r.first_name, r.other_names].filter(Boolean).join(" ")}`);
      addLine(`Date of Birth: ${r.date_of_birth}   Gender: ${r.gender}`);
      addLine(`ID No: ${r.id_no}   KRA PIN: ${r.kra_pin}`);
      addLine(`Nationality: ${r.nationality}   Ethnicity: ${r.ethnicity}`);
      addLine(`Home County: ${r.home_county}   Sub-County: ${r.sub_county}   Constituency: ${r.constituency}`);
      addLine(`Address: ${r.postal_address} ${r.postal_code} ${r.town_city}`);
      addLine(`Contacts: ${r.mobile_phone} | ${r.email_address}`);
      addLine(`Disability: ${r.disability_status} | Details: ${r.disability_details} | Reg No: ${r.disability_reg_no} | Reg Date: ${r.disability_reg_date}`);
      y += 8;
      addLine("SECTION TWO: SHORT TERM EMPLOYMENT CONTRACT AGREEMENT", 11);
      addLine(`1. Job Title: ${r.job_title}`);
      addLine(`3. Date of start of employment: ${r.start_date}`);
      addLine(`4. Period of employment (months): ${r.contract_months} (not exceeding six months)`);
      addLine(`5. Remuneration: Ksh ${r.salary_amount} payable ${r.remuneration_interval || "monthly"}`);
      addLine("8. Notice of termination of employment:");
      addLine(r.termination_clause || "Either party shall give one (1) month notice in writing or one month's salary in lieu of notice.");
      addLine(`9. Place of Work: ${r.place_of_work}`);
      addLine("10. Duties:");
      const duties = (r.duties_text || "").split(/\r?\n/).filter(Boolean);
      if (duties.length) duties.forEach((d, i) => addLine(`${i + 1}. ${d}`));
      else addLine("i. ................................................");
      addLine(`13. Work relations: You will report to ${r.reporting_to}`);
      y += 6;
      addLine("Signatures", 11);
      addLine(`Employee Name: ${r.employee_sign_name} | Date: ${r.employee_sign_date}`);
      addLine(`Employee Witness: ${r.employee_witness_name} | Date: ${r.employee_witness_date}`);
      addLine(`Authorized Officer: ${r.authorized_officer_name} (${r.authorized_officer_designation}) | Date: ${r.authorized_officer_sign_date}`);
      addLine(`Government Witness: ${r.gov_witness_name} (${r.gov_witness_designation}) | Date: ${r.gov_witness_date}`);
      y += 8;
      addLine("NOTE: All alterations, additions and deletions must be initialed by both parties and their witnesses.", 9, 14);

      const name = [r.surname, r.first_name, r.other_names].filter(Boolean).join("-").replace(/\s+/g, "_") || "contract";
      doc.save(`short-term-employment-${name}.pdf`);
    },
    downloadForm(row) {
      const r = { ...makeDefaultForm(), ...row };
      const duties = (r.duties_text || "").split(/\r?\n/).filter((x) => x.trim() !== "");
      const dutiesHtml = duties.length
        ? duties.map((d, i) => `<div>${["i", "ii", "iii", "iv", "v"][i] || (i + 1)}. ${this.esc(d)}</div>`).join("")
        : "<div>i. ....................................................</div><div>ii. ....................................................</div><div>iii. ....................................................</div>";

      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Short Term Employment Contract</title>
      <style>body{font-family:Arial,sans-serif;color:#111;padding:24px;line-height:1.45}h3,h4{margin:0 0 8px} .t{margin-top:10px}
      .line{border-bottom:1px dotted #444;display:inline-block;min-width:240px;padding:0 4px} .small{font-size:12px;color:#333}
      .sig{margin-top:14px;padding-top:10px;border-top:1px solid #999} .two{display:flex;gap:20px}.two>div{flex:1}</style></head><body>
      <h4>REPUBLIC OF KENYA</h4><h3>SHORT TERM EMPLOYMENT CONTRACT FORM (PSC 38A)</h3>
      <div class="t"><b>Ministry/Department/Agency/County:</b> <span class="line">${this.esc(r.mda_county)}</span></div>
      <div class="t"><b>SECTION ONE: PERSONAL DETAILS</b></div>
      <div>Surname: <span class="line">${this.esc(r.surname)}</span> First Name: <span class="line">${this.esc(r.first_name)}</span> Other Name(s): <span class="line">${this.esc(r.other_names)}</span></div>
      <div>Date of Birth: <span class="line">${this.esc(r.date_of_birth)}</span> Gender: <span class="line">${this.esc(r.gender)}</span></div>
      <div>ID No: <span class="line">${this.esc(r.id_no)}</span> KRA Tax PIN: <span class="line">${this.esc(r.kra_pin)}</span></div>
      <div>Nationality: <span class="line">${this.esc(r.nationality)}</span> Ethnicity: <span class="line">${this.esc(r.ethnicity)}</span> Home County: <span class="line">${this.esc(r.home_county)}</span></div>
      <div>Sub-County: <span class="line">${this.esc(r.sub_county)}</span> Constituency: <span class="line">${this.esc(r.constituency)}</span></div>
      <div>Postal Address: <span class="line">${this.esc(r.postal_address)}</span> Code: <span class="line">${this.esc(r.postal_code)}</span> Town/City: <span class="line">${this.esc(r.town_city)}</span></div>
      <div>Mobile Phone No: <span class="line">${this.esc(r.mobile_phone)}</span> E-mail: <span class="line">${this.esc(r.email_address)}</span></div>
      <div>Disability: <span class="line">${this.esc(r.disability_status)}</span> Details: <span class="line">${this.esc(r.disability_details)}</span></div>
      <div>Registration No: <span class="line">${this.esc(r.disability_reg_no)}</span> Registration Date: <span class="line">${this.esc(r.disability_reg_date)}</span></div>
      <div class="t"><b>SECTION TWO: CONTRACT AGREEMENT</b></div>
      <div>1. Job Title: <span class="line">${this.esc(r.job_title)}</span></div>
      <div>3. Start Date: <span class="line">${this.esc(r.start_date)}</span></div>
      <div>4. Period of Employment (Months): <span class="line">${this.esc(r.contract_months)}</span> (Max 6 months)</div>
      <div>5. Remuneration: Ksh <span class="line">${this.esc(r.salary_amount)}</span> payable <span class="line">${this.esc(r.remuneration_interval)}</span></div>
      <div>8. Notice of termination: ${this.nl(r.termination_clause)}</div>
      <div>9. Place of Work: <span class="line">${this.esc(r.place_of_work)}</span></div>
      <div>10. Duties:</div><div>${dutiesHtml}</div>
      <div>13. Work relations: You will report to <span class="line">${this.esc(r.reporting_to)}</span></div>
      <div class="sig two"><div><b>Employee</b><br>Name: ${this.esc(r.employee_sign_name)}<br>Date: ${this.esc(r.employee_sign_date)}<br>Witness: ${this.esc(r.employee_witness_name)}<br>Witness Date: ${this.esc(r.employee_witness_date)}</div>
      <div><b>On behalf of Government</b><br>Authorized Officer: ${this.esc(r.authorized_officer_name)}<br>Designation: ${this.esc(r.authorized_officer_designation)}<br>Date: ${this.esc(r.authorized_officer_sign_date)}<br>Witness: ${this.esc(r.gov_witness_name)} (${this.esc(r.gov_witness_designation)})<br>Witness Date: ${this.esc(r.gov_witness_date)}</div></div>
      <p class="small">NOTE: All alterations, additions and deletions must be initialed by both parties and witnesses.</p>
      </body></html>`;

      const w = window.open("", "_blank");
      if (!w) return;
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    }
  }
};
</script>

<style scoped>
.short-term-wrap {
  width: 100%;
}
.form-label {
  font-weight: 600;
}
</style>
